import { motion } from "framer-motion";
import { CheckCircle2, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { AfterpayToday } from "@/components/booking/PayLaterCalculator";
import { JR_PACKAGES } from "./juniorData";

const PackageCard = ({ p, i, onSelect }) => (
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.6, delay: i * 0.12 }}
    className={`relative rounded-3xl p-7 md:p-9 border-2 ${p.ring} ${p.best ? "bg-gradient-to-br from-lime-300/[0.08] via-dream-surface to-pink-500/[0.08]" : "bg-dream-surface/70"} flex flex-col`}
    data-testid={`jr-package-${p.id}`}
  >
    {p.best && (
      <span className="absolute -top-4 left-7 inline-flex items-center gap-1 px-4 py-1.5 rounded-full bg-gradient-to-r from-lime-300 to-pink-400 text-black text-xs font-extrabold uppercase tracking-wider rotate-[-2deg] shadow-lg">
        <Star className="w-3.5 h-3.5 fill-black" /> Best value
      </span>
    )}
    <p className="text-white/50 text-xs font-mono uppercase tracking-widest">{p.tagline}</p>
    <h3 className="font-display text-2xl md:text-3xl font-bold text-white mt-2">{p.name}</h3>
    <div className="flex items-end gap-3 mt-5">
      <span className={`font-display text-6xl font-bold text-transparent bg-clip-text bg-gradient-to-r ${p.accent}`} data-testid={`jr-package-price-${p.id}`}>${p.price}</span>
      <span className="text-white/60 text-sm mb-2">{p.duration}{p.sessions > 1 ? ` · ${p.sessions} sessions` : ""}</span>
    </div>
    <AfterpayToday price={p.price} className="mt-1" testId={`jr-package-afterpay-${p.id}`} />
    <ul className="space-y-2.5 mt-6 mb-8">
      {p.features.map((f) => (
        <li key={f} className={`flex items-start gap-2.5 text-sm ${f.includes("FREE") ? "text-lime-300 font-semibold" : "text-white/85"}`}>
          <CheckCircle2 className="w-4 h-4 text-lime-300 flex-shrink-0 mt-0.5" /> {f}
        </li>
      ))}
    </ul>
    <Button
      onClick={() => onSelect(p.id)}
      className={`mt-auto rounded-full py-6 font-bold ${p.best ? "bg-lime-300 hover:bg-lime-200 text-black" : "bg-white/10 hover:bg-white/20 text-white border border-white/20"}`}
      data-testid={`jr-select-${p.id}`}
    >
      Book {p.name}
    </Button>
  </motion.div>
);

export const JuniorPackages = ({ onSelect }) => (
  <section id="jr-packages" className="relative py-20 md:py-28 bg-dream-bg scroll-mt-20 overflow-hidden" data-testid="jr-packages">
    <div className="absolute -top-32 -left-32 w-[520px] h-[520px] bg-lime-300/20 rounded-full blur-3xl pointer-events-none" />
    <div className="absolute -bottom-32 -right-32 w-[520px] h-[520px] bg-pink-500/25 rounded-full blur-3xl pointer-events-none" />
    <div className="relative max-w-6xl mx-auto px-6">
      <div className="max-w-2xl mb-14">
        <p className="font-mono text-xs tracking-[0.3em] uppercase text-lime-300 mb-4">Pick your package</p>
        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight">Two ways to start mixing.</h2>
        <p className="text-white/60 mt-4">Simple prices, everything included. Afterpay &amp; Zip Pay available.</p>
      </div>
      <div className="grid md:grid-cols-2 gap-8">
        {JR_PACKAGES.map((p, i) => <PackageCard key={p.id} p={p} i={i} onSelect={onSelect} />)}
      </div>
    </div>
  </section>
);
